/**
 * V2 Transaction API Service
 * 트랜잭션 조회, 전송 등 트랜잭션 관련 기능
 */

import OtpVerificationError from '@/erros/OtpVerificationError'
import { apiInterceptor } from '@/networks/api/interceptor'

/**
 * 트랜잭션 상태
 */
export enum TransactionStatus {
  PENDING = 'PENDING',
  CONFIRMED = 'CONFIRMED',
  FAILED = 'FAILED',
}

/**
 * 트랜잭션 데이터 인터페이스
 */
export interface ITransaction {
  id: string
  accountId: string
  networkId?: string
  txHash: string
  fromAddress: string | string[] // 백엔드는 배열로 반환
  toAddress: string | string[] // 백엔드는 배열로 반환
  amount: string
  fee: string
  status: TransactionStatus
  transactionType?: 'OUT' | 'IN' // 백엔드 필드
  direction?: 'SEND' | 'RECEIVE' // 프론트엔드 편의용 (변환됨)
  blockNumber?: number
  blockHeight?: number // 백엔드는 blockHeight 사용
  blockTime?: Date
  transactionDate?: Date // 백엔드 필드
  metadata?: any
  createdAt: Date
  updatedAt?: Date
}

/**
 * 트랜잭션 조회 파라미터
 */
export interface IGetTransactionsParams {
  accountIds?: string | string[]
  txIds?: string | string[]
  txHashes?: string | string[]
  statuses?: TransactionStatus | TransactionStatus[]
  limit?: number
  offset?: number
}

/**
 * 트랜잭션 준비 파라미터
 */
export interface IPrepareTransactionParams {
  accountId: string
  type: 'transfer' | 'contract' // 필수: transaction type
  recipient: string // 받는 주소 (toAddress -> recipient)
  amount: string
  sender?: string
  recipientDomain?: string // ENS 도메인
  convertedRecipient?: string // Harmony 등에서 사용
  family?: string
  data?: string // 메모 또는 트랜잭션 데이터
  nonce?: number
  preparedFee?: any
  nft?: any
  extra?: any
}

/**
 * 트랜잭션 서명 파라미터
 * 백엔드 DTO: accountId, transaction, UVD 필요
 * sender는 transaction 안에 포함되어 있음
 */
export interface ISignTransactionParams {
  accountId: string
  transaction: any // prepareTransaction 응답의 transaction 전체
  UVD: {
    b64SKey: string
    b64Data: string
  }
  otpCode?: {
    code: string
    isRecoveryCode?: boolean
  }
}

/**
 * 트랜잭션 브로드캐스트 파라미터
 */
export interface IBroadcastTransactionParams {
  accountId: string
  signedOperation: any // signTransaction 응답의 전체 data
}

/**
 * 주소 검증 파라미터
 */
export interface IValidateAddressParams {
  accountId: string
  toAddress: string
  isContract?: boolean
}

/**
 * 트랜잭션 목록 응답
 */
export interface IGetTransactionsResponse {
  transactions: ITransaction[]
  pagination?: {
    total: string
    limit: string
    offset: string
  }
}

/**
 * 트랜잭션 준비 응답 - 수수료 옵션
 */
export interface IPreparedFee {
  high: {
    gasPrice: string
    gasLimit: string
    fee: string
  }
  medium: {
    gasPrice: string
    gasLimit: string
    fee: string
  }
  low: {
    gasPrice: string
    gasLimit: string
    fee: string
  }
}

/**
 * 트랜잭션 준비 응답
 */
export interface IPrepareTransactionResponse {
  accountId: string // 계정 ID
  sender: string // 발신자 주소
  transaction: any // 준비된 트랜잭션 객체
  fees: IPreparedFee // 수수료 옵션 (high/medium/low) - 프론트엔드 전용
  estimatedTime?: {
    high: string
    medium: string
    low: string
  }
}

/**
 * 트랜잭션 서명 응답
 */
export interface ISignTransactionResponse {
  signedTransaction: any // 서명된 트랜잭션
  txHash?: string // 트랜잭션 해시 (선택)
}

export interface ITransactionAPIService {
  getTransactions(
    params: IGetTransactionsParams,
  ): Promise<IGetTransactionsResponse>
  prepareTransaction(
    params: IPrepareTransactionParams,
  ): Promise<IPrepareTransactionResponse>
  signTransaction(
    params: ISignTransactionParams,
  ): Promise<ISignTransactionResponse>
  broadcastTransaction(
    params: IBroadcastTransactionParams,
  ): Promise<ITransaction>
  validateAddress(
    params: IValidateAddressParams,
  ): Promise<{ isValid: boolean; message?: string; errorType?: string }>
  getTransactionReceipt(params: {
    txIds?: string | string[]
    accountIds?: string | string[]
    txHashes?: string | string[]
  }): Promise<any>
}

export class TransactionAPIService implements ITransactionAPIService {
  /**
   * 트랜잭션 목록 조회 (V2)
   * V1: GET /transaction/me
   * V2: GET /transaction
   */
  async getTransactions(
    params: IGetTransactionsParams,
  ): Promise<IGetTransactionsResponse> {
    const queryParams = new URLSearchParams()

    // accountIds 파라미터 처리
    if (params.accountIds) {
      const accountIdsArray = Array.isArray(params.accountIds)
        ? params.accountIds
        : [params.accountIds]
      accountIdsArray.forEach((id) => queryParams.append('accountIds', id))
    }

    // txIds 파라미터 처리
    if (params.txIds) {
      const txIdsArray = Array.isArray(params.txIds)
        ? params.txIds
        : [params.txIds]
      txIdsArray.forEach((id) => queryParams.append('txIds', id))
    }

    // txHashes 파라미터 처리
    if (params.txHashes) {
      const txHashesArray = Array.isArray(params.txHashes)
        ? params.txHashes
        : [params.txHashes]
      txHashesArray.forEach((hash) => queryParams.append('txHashes', hash))
    }

    // statuses 파라미터 처리
    if (params.statuses) {
      const statusesArray = Array.isArray(params.statuses)
        ? params.statuses
        : [params.statuses]
      statusesArray.forEach((status) => queryParams.append('statuses', status))
    }

    // V2: Pagination 파라미터는 백엔드에서 지원하지 않음
    // if (params.limit) {
    //   queryParams.append('limit', params.limit.toString());
    // }
    // if (params.offset) {
    //   queryParams.append('offset', params.offset.toString());
    // }

    const queryString = queryParams.toString()
    const endpoint = queryString
      ? `/transaction?${queryString}`
      : '/transaction'

    const response = await apiInterceptor.request(endpoint)
    if (!response.ok) {
      throw new Error(`Failed to fetch transactions: ${response.statusText}`)
    }

    const data = await response.json()

    // V2 응답 구조 처리
    let transactions: ITransaction[] = []

    if (data.success && data.data && data.data.transactions) {
      transactions = data.data.transactions
    } else if (data.transactions) {
      transactions = data.transactions
    }

    // 백엔드 응답 데이터를 프론트엔드 형식으로 변환
    const normalizedTransactions = transactions.map((tx) => ({
      ...tx,
      // transactionType을 direction으로 매핑
      direction:
        tx.transactionType === 'OUT'
          ? ('SEND' as const)
          : tx.transactionType === 'IN'
            ? ('RECEIVE' as const)
            : tx.direction || ('SEND' as const),
      // 배열인 경우 첫 번째 요소 사용 (UI에서 문자열 기대)
      fromAddress: Array.isArray(tx.fromAddress)
        ? tx.fromAddress[0]
        : tx.fromAddress,
      toAddress: Array.isArray(tx.toAddress) ? tx.toAddress[0] : tx.toAddress,
    }))

    return {
      transactions: normalizedTransactions,
      pagination: data.data?.pagination || data.pagination,
    }
  }

  /**
   * 트랜잭션 준비 (V2) - Step 1
   * V1: GET /tx/prepare
   * V2: POST /transaction/prepare
   *
   * 수수료 견적(high/medium/low)을 조회합니다.
   */
  async prepareTransaction(
    params: IPrepareTransactionParams,
  ): Promise<IPrepareTransactionResponse> {
    const response = await apiInterceptor.request('/transaction/prepare', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(params),
    })

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(
        errorData.error?.message ||
          `Failed to prepare transaction: ${response.statusText}`,
      )
    }

    const data = await response.json()

    // V2 응답 구조 처리 및 변환
    if (data.success && data.data) {
      const responseData = data.data
      const preparedFee = responseData.transaction?.preparedFee

      // 백엔드 응답 구조를 프론트엔드 예상 구조로 변환
      if (preparedFee) {
        return {
          accountId: responseData.accountId,
          sender: responseData.transaction?.sender || responseData.sender, // sender는 transaction 안에 있음
          transaction: responseData.transaction,
          fees: {
            high: {
              gasPrice: preparedFee.high || preparedFee.fee || '0',
              gasLimit: preparedFee.gasLimit || '21000',
              fee: preparedFee.high || preparedFee.fee || '0',
            },
            medium: {
              gasPrice: preparedFee.medium || preparedFee.fee || '0',
              gasLimit: preparedFee.gasLimit || '21000',
              fee: preparedFee.medium || preparedFee.fee || '0',
            },
            low: {
              gasPrice: preparedFee.low || preparedFee.fee || '0',
              gasLimit: preparedFee.gasLimit || '21000',
              fee: preparedFee.low || preparedFee.fee || '0',
            },
          },
        }
      }

      return responseData
    }

    return data
  }

  /**
   * 트랜잭션 서명 (V2) - Step 2
   * V1: POST /tx/sign
   * V2: POST /transaction/sign
   *
   * 사용자가 선택한 수수료로 트랜잭션을 서명합니다.
   */
  async signTransaction(
    params: ISignTransactionParams,
  ): Promise<ISignTransactionResponse> {
    const response = await apiInterceptor.request('/transaction/sign', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(params),
    })

    if (!response.ok) {
      const errorData = await response.json()
      // OTP 오류인 경우 OtpVerificationError throw
      if (
        errorData.error?.code === 'UNAUTHORIZED' &&
        (errorData.error?.message?.includes('verification') ||
          errorData.error?.message?.includes('OTP') ||
          errorData.error?.message?.includes('2FA'))
      ) {
        throw new OtpVerificationError(errorData.error.message)
      }
      // 그 외 에러
      throw new Error(
        errorData.error?.message ||
          `Failed to sign transaction: ${response.statusText}`,
      )
    }

    const data = await response.json()

    // V2 응답 구조 처리
    if (data.success && data.data) {
      return data.data
    }

    return data
  }

  /**
   * 트랜잭션 브로드캐스트 (V2) - Step 3
   * V1: POST /tx/broadcast
   * V2: POST /transaction/broadcast
   *
   * 서명된 트랜잭션을 블록체인에 브로드캐스트합니다.
   */
  async broadcastTransaction(
    params: IBroadcastTransactionParams,
  ): Promise<ITransaction> {
    const response = await apiInterceptor.request('/transaction/broadcast', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        broadcastTransactionRequest: params,
      }),
    })

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(
        errorData.error?.message ||
          `Failed to broadcast transaction: ${response.statusText}`,
      )
    }

    const data = await response.json()

    // V2 응답 구조 처리
    if (data.success && data.data) {
      // E2E 테스트를 보면 data.data.transaction이 실제 transaction 객체
      return data.data.transaction || data.data
    }

    return data
  }

  /**
   * 주소 유효성 검증 (V2)
   * V1: 프론트엔드에서 체크
   * V2: GET /transaction/validate-address
   */
  async validateAddress(
    params: IValidateAddressParams,
  ): Promise<{ isValid: boolean; message?: string; errorType?: string }> {
    const queryParams = new URLSearchParams({
      accountId: params.accountId,
      toAddress: params.toAddress,
    })

    if (params.isContract !== undefined) {
      queryParams.append('isContract', params.isContract.toString())
    }

    const response = await apiInterceptor.request(
      `/transaction/validate-address?${queryParams.toString()}`,
    )

    if (!response.ok) {
      return {
        isValid: false,
        message: `Address validation failed: ${response.statusText}`,
      }
    }

    const data = await response.json()

    // V2 응답 구조 처리
    if (data.success && data.data) {
      return {
        isValid: data.data.isValid ?? false,
        message: data.data.isValid
          ? data.message
          : data.data.errorType || 'Invalid address',
        errorType: data.data.errorType,
      }
    }

    return {
      isValid: false,
      message: data.message || 'Address validation failed',
    }
  }

  /**
   * 트랜잭션 영수증 조회 (V2)
   * V1: 없음
   * V2: GET /transaction/receipt
   */
  async getTransactionReceipt(params: {
    txIds?: string | string[]
    accountIds?: string | string[]
    txHashes?: string | string[]
  }): Promise<any> {
    const queryParams = new URLSearchParams()

    // 배열 또는 단일 문자열을 txIds=a&txIds=b 형식으로 전송
    if (params.txIds) {
      const txIdsArray = Array.isArray(params.txIds)
        ? params.txIds
        : [params.txIds]
      txIdsArray.forEach((id) => queryParams.append('txIds', id))
    }
    if (params.accountIds) {
      const accountIdsArray = Array.isArray(params.accountIds)
        ? params.accountIds
        : [params.accountIds]
      accountIdsArray.forEach((id) => queryParams.append('accountIds', id))
    }
    if (params.txHashes) {
      const txHashesArray = Array.isArray(params.txHashes)
        ? params.txHashes
        : [params.txHashes]
      txHashesArray.forEach((hash) => queryParams.append('txHashes', hash))
    }

    const response = await apiInterceptor.request(
      `/transaction/receipt?${queryParams.toString()}`,
    )

    if (!response.ok) {
      throw new Error(
        `Failed to fetch transaction receipt: ${response.statusText}`,
      )
    }

    const data = await response.json()

    // V2 응답 구조 처리
    if (data.success && data.data) {
      return data.data
    }

    return data
  }
}

// 싱글톤 인스턴스
export const transactionAPIServiceV2 = new TransactionAPIService()
